import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
  Paper
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

/**
 * PROCUREMENT AND STORE workflow guide bodies.
 * Consumed by WorkflowGuide.js via STORE_VARIANT_CONFIG.
 */

const StepCard = ({ index, title, who, detail, result, where }) => (
  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper', borderColor: 'divider' }}>
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.85rem',
          fontWeight: 700,
          mt: 0.25
        }}
      >
        {index}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          <Chip label={who} size="small" color="primary" variant="outlined" />
          {where ? (
            <Chip label={where} size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {detail}
        </Typography>
        <Typography variant="caption" color="text.primary">
          <Box component="span" fontWeight={600}>
            Result:{' '}
          </Box>
          {result}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const FlowArrow = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
    <ArrowDownwardIcon sx={{ color: 'text.disabled' }} fontSize="small" />
  </Box>
);

const MiniFlow = ({ title, steps }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    <Stack spacing={0.5}>
      {steps.map((step, i) => (
        <React.Fragment key={`${title}-${i}`}>
          <Box
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {step.label}
            </Typography>
            <Typography variant="caption" color="primary.main">
              {step.who}
            </Typography>
          </Box>
          {i < steps.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>
  </Paper>
);

const StatusStrip = ({ statuses }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 0.5,
      p: 1.5,
      borderRadius: 1,
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    {statuses.map((status, i) => (
      <React.Fragment key={status}>
        <Chip label={status} size="small" color={i === statuses.length - 1 ? 'success' : 'default'} />
        {i < statuses.length - 1 ? (
          <ArrowForwardIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
        ) : null}
      </React.Fragment>
    ))}
  </Box>
);

const Steps = ({ steps }) => (
  <Stack spacing={0}>
    {steps.map((step, i) => (
      <React.Fragment key={step.title}>
        <StepCard index={i + 1} {...step} />
        {i < steps.length - 1 ? <FlowArrow /> : null}
      </React.Fragment>
    ))}
  </Stack>
);

const Chips = ({ items }) => (
  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
    {items.map((label) => (
      <Chip key={label} label={label} size="small" />
    ))}
  </Stack>
);

const GuideShell = ({ intro, statusTitle, statuses, statusNote, stepsTitle = 'Step-by-step', steps, extra }) => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {intro}
    </Typography>
    {statuses ? (
      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {statusTitle || 'Status / path'}
        </Typography>
        <StatusStrip statuses={statuses} />
        {statusNote ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {statusNote}
          </Typography>
        ) : null}
      </Box>
    ) : null}
    <Divider />
    <Typography variant="subtitle2" fontWeight={700}>
      {stepsTitle}
    </Typography>
    <Steps steps={steps} />
    {extra}
  </Stack>
);

const StoreActivitiesBody = () => (
  <GuideShell
    intro="Store Activities is the store hub: dashboard, work-order INTENT requests, stock reconciliation, tool control, parts lookup, access logs, and loan approvals. Buy → receive → stock → issue lives on the other Procurement & Store pages."
    statusTitle="Typical chain across store modules"
    statuses={['Procurement', 'Receiving', 'Inventory', 'Issuance', 'Returns']}
    steps={[
      {
        title: 'Check Dashboard',
        who: 'Store',
        detail: 'See stock health, expiry risks, and activity that needs attention.',
        result: 'Know what to work next',
        where: 'Dashboard tab'
      },
      {
        title: 'Respond to INTENT requests',
        who: 'Store',
        detail:
          'Accept or reject parts needs from Maintenance / Planning. Shortages can lead you to create or review a Purchase Order.',
        result: 'WO store gate cleared or escalated to Procurement',
        where: 'INTENT Request'
      },
      {
        title: 'Reconcile stock when needed',
        who: 'Store',
        detail: 'Run Cycle Count or Full Audit; complete when counts are finished.',
        result: 'IN_PROGRESS → COMPLETED',
        where: 'Reconciliation'
      },
      {
        title: 'Manage tools',
        who: 'Store',
        detail: 'Track calibration, issue, and return of tools.',
        result: 'Tool assignment & calibration recorded',
        where: 'Tool Control'
      },
      {
        title: 'Approve loan-outs (when pending)',
        who: 'Store Inspector → AM',
        detail:
          'Loan Out approvals for lending company stock live under Loan Items (not on the standalone Loan page).',
        result: 'PENDING_STORE_APPROVAL → PENDING_AM_APPROVAL → APPROVED',
        where: 'Loan Items → Approvals'
      }
    ]}
    extra={
      <>
        <Divider />
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Other tabs
        </Typography>
        <Chips
          items={[
            'Parts — catalog / lookup',
            'Access Logs — who entered store areas',
            'Loan Items — inventory + loan-in/out + Approvals'
          ]}
        />
      </>
    }
  />
);

const StoreInventoryBody = () => (
  <GuideShell
    intro="Inventory is the live stock book: what is on the shelf, where it sits, and its status. Receiving adds stock; Issuance and Loan Out remove it; Returns and loan returns put it back."
    statusTitle="Common item statuses"
    statuses={['IN_INVENTORY', 'ISSUED', 'QUARANTINED', 'RETURNED', 'UNAVAILABLE']}
    statusNote="Store types include BONDED, POL, QUARANTINE, and SCRAPE."
    steps={[
      {
        title: 'Find the stock line',
        who: 'Store',
        detail: 'Filter by status, location, store type, or aircraft.',
        result: 'Target inventory row',
        where: 'Inventory list'
      },
      {
        title: 'Open Details / History',
        who: 'Store',
        detail: 'See quantities and the audit trail with links back to receiving, issuance, and other movements.',
        result: 'Full quantity story',
        where: 'Detail modal'
      },
      {
        title: 'Update or quarantine',
        who: 'Store',
        detail: 'Edit qty/location/status/notes, or quarantine / start a store transfer when shelf life is at risk.',
        result: 'Audited update or QUARANTINED / transfer started',
        where: 'Inventory actions'
      },
      {
        title: 'Archive unused lines when appropriate',
        who: 'Store',
        detail: 'Soft-archive; restore if needed. Permanent delete only after archive rules are met.',
        result: 'List stays clean without losing history',
        where: 'Inventory actions'
      }
    ]}
    extra={
      <>
        <Divider />
        <Chips
          items={[
            'Fed by Receiving and Inspection',
            'Depleted by Issuance / Loan Out',
            'Restored by Returns / loan returns'
          ]}
        />
      </>
    }
  />
);

const StoreReturnsBody = () => (
  <GuideShell
    intro="Store Returns brings unused or leftover parts back from an Issuance (work order / dispatch). Loan returns use Receiving → Loan/Rental instead — not this page."
    statusTitle="Issuance status after returns"
    statuses={['ISSUED', 'PARTIAL_RETURN', 'RETURNED']}
    steps={[
      {
        title: 'Open Process Return',
        who: 'Store',
        detail: 'Pick an open issuance (ISSUED or PARTIAL_RETURN) that still has remaining quantity.',
        result: 'Lines available to return',
        where: 'Process Return tab'
      },
      {
        title: 'Select lines, qty, and condition',
        who: 'Store',
        detail: 'Enter how much is coming back and its condition (serviceable / unserviceable).',
        result: 'Return payload ready',
        where: 'Process Return'
      },
      {
        title: 'Submit the return',
        who: 'Store',
        detail: 'Stock is restored to inventory. Unserviceable items may be quarantined.',
        result: 'Issuance → PARTIAL_RETURN or RETURNED',
        where: 'Process Return'
      },
      {
        title: 'Review Returns History',
        who: 'Store',
        detail: 'Audit completed and partial returns.',
        result: 'Traceable return history',
        where: 'Returns History tab'
      }
    ]}
  />
);

const ReceivingBody = () => (
  <GuideShell
    intro="Receiving and Inspection is how parts enter inventory. The main path is Against a Purchase Order. You can also receive Loan/Rental items or stock From Aircraft. Inventory is applied only after inspection approval."
    statusTitle="Receiving status"
    statuses={['DRAFT', 'PENDING_INSPECTOR_APPROVAL', 'APPROVED']}
    statusNote="Reject returns the batch for correction. APPROVED applies stock and updates the PO."
    steps={[
      {
        title: 'Choose how you are receiving',
        who: 'Store',
        detail: 'Against PO (normal), Loan/Rental (borrow or loan return), or From Aircraft (removal into store).',
        result: 'Correct form + history filter',
        where: 'Select workflow'
      },
      {
        title: 'Select the PO (Against PO path)',
        who: 'Store',
        detail: 'Pick an ISSUED or PARTIALLY_RECEIVED purchase order. Ordered vs already-received quantities are shown.',
        result: 'Lines ready to receive',
        where: 'Against PO'
      },
      {
        title: 'Enter receipt details',
        who: 'Store Executive',
        detail: 'Qty now, condition, cert/batch/serial, location, and store type.',
        result: 'Validated receipt batch',
        where: 'Receiving form'
      },
      {
        title: 'Submit for inspection',
        who: 'Store Executive',
        detail: 'No inventory change yet — waiting for Inspector (or AM) approval.',
        result: 'PENDING_INSPECTOR_APPROVAL',
        where: 'Submit'
      },
      {
        title: 'Inspector / AM approves',
        who: 'Store Inspector',
        detail: 'Approve applies inventory and moves the PO toward PARTIALLY_RECEIVED → RECEIVED → CLOSED.',
        result: 'Stock in Inventory · PO updated',
        where: 'Inspection approval'
      },
      {
        title: 'Attach certificates',
        who: 'Store',
        detail: 'Upload documents for traceability on the receiving record.',
        result: 'Docs on file',
        where: 'Documents'
      }
    ]}
    extra={
      <>
        <Divider />
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Alternate paths
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5
          }}
        >
          <MiniFlow
            title="Loan / Rental"
            steps={[
              { label: 'Receive borrow or process loan return', who: 'Store' },
              { label: 'Inspector approves', who: 'Inspector' },
              { label: 'Inventory updated / loan balance cleared', who: 'System' }
            ]}
          />
          <MiniFlow
            title="From Aircraft"
            steps={[
              { label: 'Record removal into store', who: 'Store' },
              { label: 'Inspect & approve', who: 'Inspector' },
              { label: 'Stock without a PO', who: 'Inventory' }
            ]}
          />
        </Box>
      </>
    }
  />
);

const IssuanceBody = () => (
  <GuideShell
    intro="Issuance covers two jobs: Issue & Dispatch (give parts to a person / WO) and Store Transfer (move stock between store locations, e.g. into Quarantine)."
    statusTitle="Issue & Dispatch statuses"
    statuses={['ISSUED', 'PARTIAL_RETURN', 'RETURNED']}
    steps={[
      {
        title: 'Issue & Dispatch — set recipient',
        who: 'Store',
        detail: 'Choose internal user or external recipient; optionally link a work order.',
        result: 'Dispatch context set',
        where: 'Issue & Dispatch'
      },
      {
        title: 'Add IN_INVENTORY lines and quantities',
        who: 'Store',
        detail: 'Build the issue basket from available stock. Optional delivery note for the challan.',
        result: 'Issue basket ready',
        where: 'Issue & Dispatch'
      },
      {
        title: 'Issue Items',
        who: 'Store',
        detail: 'Confirm issue (with or without WO). Stock is reduced; record status becomes ISSUED.',
        result: 'Parts out · inventory reduced',
        where: 'Issue Items'
      },
      {
        title: 'Unused parts come back via Returns',
        who: 'Store',
        detail: 'Process returns on the Store Returns page — not on Issuance.',
        result: 'PARTIAL_RETURN or RETURNED',
        where: '/store/returns'
      },
      {
        title: 'Store Transfer (other tab)',
        who: 'Store → Inspector',
        detail: 'Submit a location move → PENDING_STORE_APPROVAL → Inspector approves → stock relocated.',
        result: 'Transfer APPROVED (or REJECTED)',
        where: 'Store Transfer tab'
      }
    ]}
  />
);

const ProcurementBody = () => (
  <GuideShell
    intro="Procurement turns demand into Purchase Orders. Reorder Requests, Independent PO, and WO Requirement capture need; POs go through Store then Accountable Manager approval; goods arrive only via Receiving and Inspection."
    statusTitle="Purchase Order status (happy path)"
    statuses={[
      'DRAFT',
      'PENDING_STORE_APPROVAL',
      'PENDING_AM_APPROVAL',
      'ISSUED',
      'PARTIALLY_RECEIVED',
      'RECEIVED',
      'CLOSED'
    ]}
    steps={[
      {
        title: 'Capture demand',
        who: 'Store / System',
        detail:
          'Create a reorder (manual or low-stock), review Independent PO from Planning, or WO Requirement from work-order / planning needs.',
        result: 'Demand PENDING (or similar)',
        where: 'Reorder Requests · Independent PO · WO Requirement'
      },
      {
        title: 'Create the Purchase Order',
        who: 'Store',
        detail: 'Manual PO or from selected reorders — operator, aircraft, supplier, lines, priority.',
        result: 'PO DRAFT',
        where: 'Purchase Orders'
      },
      {
        title: 'Submit for Store approval',
        who: 'Store Executive',
        detail: 'Send the draft into the approval chain.',
        result: 'PENDING_STORE_APPROVAL',
        where: 'Purchase Orders'
      },
      {
        title: 'Store Inspector approves',
        who: 'Store Inspector',
        detail: 'Approve advances to AM; reject returns to DRAFT.',
        result: 'PENDING_AM_APPROVAL',
        where: 'PO detail'
      },
      {
        title: 'Accountable Manager approves',
        who: 'AM',
        detail: 'PO is issued to the supplier.',
        result: 'ISSUED',
        where: 'PO detail'
      },
      {
        title: 'Receive against the PO',
        who: 'Store',
        detail: 'Use Receiving and Inspection → Against PO. Partial receipts allowed until fully received and closed.',
        result: 'PARTIALLY_RECEIVED → RECEIVED → CLOSED',
        where: '/receiving-and-inspection'
      }
    ]}
    extra={
      <>
        <Divider />
        <Chips
          items={[
            'Reorder: PENDING → APPROVED → ORDERED → DELIVERED',
            'WO Requirement mirrors Store work-order material needs',
            'Cancel anytime when rules allow → CANCELLED'
          ]}
        />
      </>
    }
  />
);

const LoanBody = () => (
  <GuideShell
    intro="Loan covers borrowing (Loan In) and lending (Loan Out). Physical receive/return always goes through Receiving and Inspection → Loan/Rental. Loan-out approvals are on Store Activities → Loan Items, not on this page."
    statusTitle="Loan Out approval path"
    statuses={['PENDING_STORE_APPROVAL', 'PENDING_AM_APPROVAL', 'APPROVED']}
    steps={[
      {
        title: 'Loan In — borrow from outside',
        who: 'Store',
        detail: 'Enter supplier/recipient, due date, and lines; submit into the receiving/inspection pipeline.',
        result: 'Waiting inspection on Receiving',
        where: 'Loan In tab'
      },
      {
        title: 'Inspector approves borrow receipt',
        who: 'Store Inspector',
        detail: 'On Receiving → Loan/Rental, approve so borrowed stock enters inventory; history shows OPEN.',
        result: 'Borrowed stock IN_INVENTORY',
        where: 'Receiving'
      },
      {
        title: 'Return borrowed items when due',
        who: 'Store',
        detail: 'Start return from Loan — navigates to Receiving with LOAN_IN_RETURN prefill.',
        result: 'Return receipt closes outstanding',
        where: 'Loan History / Receiving'
      },
      {
        title: 'Loan Out — lend company stock',
        who: 'Store',
        detail: 'Pick owned IN_INVENTORY lines, recipient, and due date; submit for approval.',
        result: 'Pending Store then AM approval',
        where: 'Loan Out tab'
      },
      {
        title: 'Approvals then later return',
        who: 'Inspector → AM → Store',
        detail:
          'Approve on Store Activities → Loan Items. When the recipient returns goods, receive via Receiving Loan/Rental (LOAN_OUT_RETURN).',
        result: 'Stock out → later stock back · balance cleared',
        where: 'Store Activities · Receiving'
      }
    ]}
    extra={
      <>
        <Divider />
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Tabs on this page
        </Typography>
        <Chips
          items={[
            'Loan History — due dates & balances',
            'Loan In — borrow',
            'Loan Out — lend (approvals elsewhere)'
          ]}
        />
      </>
    }
  />
);

export const STORE_VARIANT_CONFIG = {
  storeActivities: {
    title: 'How Store Activities works (workflow diagram)',
    Body: StoreActivitiesBody
  },
  storeInventory: {
    title: 'How Store Inventory works (workflow diagram)',
    Body: StoreInventoryBody
  },
  storeReturns: {
    title: 'How Store Returns works (workflow diagram)',
    Body: StoreReturnsBody
  },
  receivingAndInspection: {
    title: 'How Receiving and Inspection works (workflow diagram)',
    Body: ReceivingBody
  },
  issuance: {
    title: 'How Issuance works (workflow diagram)',
    Body: IssuanceBody
  },
  procurement: {
    title: 'How Procurement works (workflow diagram)',
    Body: ProcurementBody
  },
  loan: {
    title: 'How Loan works (workflow diagram)',
    Body: LoanBody
  }
};
